import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import axios from "axios";
import {
  Bus,
  Phone,
  Navigation,
  CheckCircle2,
  XCircle,
  Clock,
  LogOut,
  Play,
  MapPin,
  Home,
} from "lucide-react";
import { toast } from "sonner";
import { useAutistaAuth } from "../../context/AutistaAuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DriveThemeSwitch from "../../components/drive/DriveThemeSwitch";
import { driveMapsUrl, haptic, openExternal } from "../../lib/drive/bridge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FASCE = [
  { id: "mattina", label: "Mattina", orario: "08:00–13:00" },
  { id: "pomeriggio", label: "Pomeriggio", orario: "13:00–18:00" },
];

const DIREZIONI = [
  { id: "andata", label: "Andata", desc: "Raccolta ospiti" },
  { id: "rientro", label: "Rientro", desc: "Accompagnamento a casa" },
];

const STATO_FERMATA_LABELS = {
  salito: "Salito a bordo",
  sceso: "Sceso a casa",
  assente: "Assente",
  ritardo: "In ritardo",
  in_attesa: "In attesa",
};

function isFermataPending(stato) {
  return !stato || stato === "in_attesa";
}

function statoFermataBadgeClass(stato) {
  if (stato === "salito" || stato === "sceso") return "bg-emerald-600";
  if (stato === "assente") return "bg-red-600";
  if (stato === "ritardo") return "bg-amber-600";
  return "bg-slate-500";
}

export default function AutistaGiro() {
  const { token, logoutActive } = useAutistaAuth();
  const [fascia, setFascia] = useState("mattina");
  const [direzione, setDirezione] = useState("andata");
  const [giro, setGiro] = useState(null);
  const [fermataIdx, setFermataIdx] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const fasciaMeta = FASCE.find((f) => f.id === fascia) || FASCE[0];
  const direzioneMeta = DIREZIONI.find((d) => d.id === direzione) || DIREZIONI[0];
  const isRientro = direzione === "rientro";

  useEffect(() => {
    setDirezione(fascia === "pomeriggio" ? "rientro" : "andata");
  }, [fascia]);

  const loadGiro = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/autista/giro-oggi`, {
        headers,
        params: { fascia, direzione },
      });
      setGiro(data.giro);
      const list = data.giro?.fermate || [];
      const idx = list.findIndex((f) => isFermataPending(f.stato_bordo));
      setFermataIdx(idx);
    } catch (e) {
      toast.error("Errore caricamento giro");
    } finally {
      setLoading(false);
    }
  }, [token, fascia, direzione]);

  useEffect(() => {
    loadGiro();
  }, [loadGiro]);

  const fermate = giro?.fermate || [];
  const fermata = fermataIdx >= 0 ? fermate[fermataIdx] : null;
  const prossima = fermataIdx >= 0 ? fermate[fermataIdx + 1] : null;
  const giroCompletato = giro?.stato === "completato" || (fermate.length > 0 && fermataIdx < 0);
  const giroAvviato = giro?.stato === "in_viaggio";

  const avviaGiro = async () => {
    if (!giro?.id || giroCompletato) return;
    try {
      await axios.post(`${API_URL}/api/autista/giro/${giro.id}/avvia`, {}, { headers });
      toast.success(`Giro ${direzioneMeta.label.toLowerCase()} avviato`);
      loadGiro();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore avvio giro");
    }
  };

  const updateStato = async (stato) => {
    if (!giro?.id || !fermata?.fascicolo_id || giroCompletato) return;
    if (!giroAvviato) {
      toast.error("Avvia il giro prima di registrare le fermate");
      return;
    }
    setActionLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/autista/giro/${giro.id}/fermate/${fermata.fascicolo_id}/stato`,
        { stato_bordo: stato },
        { headers }
      );
      haptic(stato === "salito" || stato === "sceso" ? "success" : "light");
      if (stato === "salito") toast.success("Ospite registrato a bordo");
      else if (stato === "sceso") toast.success("Ospite accompagnato a casa");
      else toast.success("Stato aggiornato");
      if (data.completato) {
        toast.success("Giro concluso — tutte le fermate gestite");
      }
      await loadGiro();
    } catch (e) {
      toast.error("Errore aggiornamento");
    } finally {
      setActionLoading(false);
    }
  };

  const naviga = () => {
    if (!fermata?.lat || !fermata?.lng) {
      toast.error("Coordinate non disponibili");
      return;
    }
    // Nessun popup / nuova finestra: bridge nativo, navigazione top-level o link utente (vedi lib/drive/bridge)
    openExternal(driveMapsUrl(fermata.lat, fermata.lng));
  };

  const chiama = () => {
    if (fermata?.telefono) openExternal(`tel:${fermata.telefono.replace(/\s/g, "")}`);
    else toast.error("Telefono non disponibile");
  };

  const oggi = format(new Date(), "EEEE d MMMM yyyy", { locale: it });
  const titoloGiro = `${fasciaMeta.label} — ${direzioneMeta.label}`;

  return (
    <div
      className="mx-auto min-h-[100dvh] max-w-lg bg-slate-50 text-slate-900 drive-safe-x dark:bg-[#18181f] dark:text-white"
      data-testid="autista-giro"
    >
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur drive-safe-top dark:border-slate-700 dark:bg-[#18181f]/95">
        <div className="flex items-center justify-between gap-2 px-4 py-2">
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-emerald-700 dark:text-emerald-400">
              Autista — {giro?.mezzo_nome || "Pulmino"}
            </p>
            <p className="text-sm capitalize text-slate-600 dark:text-slate-300">{oggi}</p>
          </div>
          <div className="flex items-center gap-1">
            <DriveThemeSwitch />
            <Button
              variant="ghost"
              size="icon"
              onClick={logoutActive}
              aria-label="Esci"
              className="h-11 w-11 text-slate-500 active:bg-slate-100 dark:text-slate-400 dark:active:bg-slate-800"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="space-y-4 p-4 drive-safe-bottom">
        <div role="tablist" aria-label="Fascia" className="flex rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800">
          {FASCE.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={fascia === f.id}
              onClick={() => setFascia(f.id)}
              className={`min-h-[48px] flex-1 rounded-lg px-2 py-2 text-center transition-colors ${
                fascia === f.id
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-slate-600 active:bg-slate-100 dark:text-slate-400 dark:active:bg-slate-700"
              }`}
            >
              <span className="block text-base font-semibold leading-tight">{f.label}</span>
              <span className="block text-xs opacity-80">{f.orario}</span>
            </button>
          ))}
        </div>

        <div role="tablist" aria-label="Direzione" className="flex rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800">
          {DIREZIONI.map((d) => (
            <button
              key={d.id}
              type="button"
              role="tab"
              aria-selected={direzione === d.id}
              onClick={() => setDirezione(d.id)}
              className={`min-h-[48px] flex-1 rounded-lg px-2 py-2 text-center transition-colors ${
                direzione === d.id
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-600 active:bg-slate-100 dark:text-slate-400 dark:active:bg-slate-700"
              }`}
            >
              <span className="block text-base font-semibold leading-tight">{d.label}</span>
              <span className="block text-xs opacity-80">{d.desc}</span>
            </button>
          ))}
        </div>

        <section className="rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 p-5 text-white shadow-lg">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Bus className="h-5 w-5" />
              <span className="font-semibold">{titoloGiro}</span>
            </div>
            {giroCompletato && <Badge className="border-0 bg-white text-emerald-800 hover:bg-white">Concluso</Badge>}
            {!giroCompletato && giro?.stato === "in_viaggio" && (
              <Badge className="border-0 bg-white/20 text-white hover:bg-white/20">In viaggio</Badge>
            )}
            {!giroCompletato && giro?.stato === "programmato" && giro && (
              <Badge className="border-0 bg-white/10 text-white hover:bg-white/10">Programmato</Badge>
            )}
          </div>
          <div className="mb-4 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-2xl font-bold">{fermate.length}</p>
              <p className="text-xs opacity-80">Ospiti</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{Math.round((giro?.distanza_m || 0) / 1000)}</p>
              <p className="text-xs opacity-80">km</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{giro?.durata_s ? Math.round(giro.durata_s / 60) : "—"}</p>
              <p className="text-xs opacity-80">min</p>
            </div>
          </div>
          {giro && !giroCompletato && giro.stato !== "in_viaggio" && fermate.length > 0 && (
            <Button
              className="h-12 w-full bg-white text-base font-semibold text-emerald-800 hover:bg-emerald-50 active:bg-emerald-100"
              onClick={avviaGiro}
            >
              <Play className="mr-2 h-5 w-5" /> Avvia {direzioneMeta.label.toLowerCase()}
            </Button>
          )}
        </section>

        {loading ? (
          <p className="py-8 text-center text-slate-500 dark:text-slate-400" role="status">Caricamento...</p>
        ) : !giro ? (
          <p className="py-8 text-center text-slate-500 dark:text-slate-400">
            Nessun giro {direzioneMeta.label.toLowerCase()} ({fasciaMeta.label.toLowerCase()}) per oggi.
            <span className="mt-2 block text-xs text-slate-500">
              Chiedi alla segreteria di generarlo dalla dashboard Centro Diurno.
            </span>
          </p>
        ) : fermate.length === 0 ? (
          <p className="py-8 text-center text-slate-500 dark:text-slate-400">Giro senza fermate programmate</p>
        ) : giroCompletato ? (
          <div className="space-y-4 rounded-2xl border border-emerald-200 bg-white p-5 dark:border-emerald-700/50 dark:bg-slate-800">
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-2 h-12 w-12 text-emerald-500 dark:text-emerald-400" />
              <h2 className="text-lg font-semibold text-emerald-700 dark:text-emerald-300">Giro concluso</h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Tutte le fermate del {direzioneMeta.label.toLowerCase()} ({fasciaMeta.label.toLowerCase()}) sono state
                gestite.
              </p>
            </div>
            <div className="space-y-2">
              {fermate.map((f, i) => (
                <div
                  key={f.fascicolo_id || i}
                  className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 p-3 dark:bg-slate-900/60"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{f.ospite_nome}</p>
                    <p className="truncate text-xs text-slate-500">{f.indirizzo}</p>
                  </div>
                  <Badge className={`shrink-0 text-white ${statoFermataBadgeClass(f.stato_bordo)}`}>
                    {STATO_FERMATA_LABELS[f.stato_bordo] || f.stato_bordo || "—"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        ) : fermata ? (
          <>
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="border-emerald-600 text-emerald-700 dark:border-emerald-500 dark:text-emerald-400">
                Fermata {fermataIdx + 1} di {fermate.length}
              </Badge>
              <span className="text-xs text-slate-500 dark:text-slate-400">{isRientro ? "Consegna a domicilio" : "Raccolta"}</span>
            </div>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-900 shadow-xl dark:border-slate-700 dark:bg-slate-800 dark:text-white">
              <div className="flex gap-4">
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-2xl font-bold text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                  {(fermata.ospite_nome || "?")[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-xl font-bold">{fermata.ospite_nome}</h2>
                  <p className="mt-1 flex items-start gap-1 text-sm text-slate-500 dark:text-slate-300">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    {fermata.indirizzo || "Indirizzo non disponibile"}
                  </p>
                  {fermata.telefono && !isRientro && (
                    <button
                      type="button"
                      onClick={chiama}
                      className="-ml-1 mt-1 inline-flex min-h-[44px] items-center px-1 text-base font-medium text-emerald-700 dark:text-emerald-400"
                    >
                      {fermata.telefono}
                    </button>
                  )}
                </div>
              </div>
              {fermata.note && (
                <p className="mt-3 rounded-lg bg-slate-50 p-2 text-sm text-slate-600 dark:bg-slate-900/60 dark:text-slate-300">{fermata.note}</p>
              )}

              {!giroAvviato && (
                <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/30 dark:text-amber-200">
                  Premi <strong>Avvia {direzioneMeta.label.toLowerCase()}</strong>{" "}
                  {isRientro ? "per registrare le consegne a domicilio." : "per registrare salite, assenze e ritardi."}
                </p>
              )}

              <Button
                className="mt-5 h-14 w-full bg-[#2E7D32] text-lg font-semibold text-white hover:bg-[#2E7D32] active:bg-[#1B5E20] disabled:opacity-50"
                onClick={() => updateStato(isRientro ? "sceso" : "salito")}
                disabled={actionLoading || !giroAvviato}
              >
                {isRientro ? (
                  <>
                    <Home className="mr-2 h-6 w-6" /> Sceso a casa
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-6 w-6" /> Salito a bordo
                  </>
                )}
              </Button>

              {!isRientro && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="h-12 border-red-200 text-base text-red-700 dark:border-red-800 dark:bg-transparent dark:text-red-300"
                    onClick={() => updateStato("assente")}
                    disabled={actionLoading || !giroAvviato}
                  >
                    <XCircle className="mr-1 h-4 w-4" /> Assente
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 border-amber-200 text-base text-amber-800 dark:border-amber-800 dark:bg-transparent dark:text-amber-300"
                    onClick={() => updateStato("ritardo")}
                    disabled={actionLoading || !giroAvviato}
                  >
                    <Clock className="mr-1 h-4 w-4" /> Ritardo
                  </Button>
                </div>
              )}
              <div className={`mt-2 grid gap-2 ${isRientro ? "grid-cols-1" : "grid-cols-2"}`}>
                {!isRientro && (
                  <Button variant="secondary" className="h-12 text-base" onClick={chiama}>
                    <Phone className="mr-1 h-4 w-4" /> Chiama
                  </Button>
                )}
                <Button variant="secondary" onClick={naviga} className={`h-12 text-base ${isRientro ? "w-full" : ""}`}>
                  <Navigation className="mr-1 h-4 w-4" /> Naviga
                </Button>
              </div>
            </section>

            {prossima && isFermataPending(prossima.stato_bordo) && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/80">
                <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">Prossima fermata</p>
                <p className="font-medium">{prossima.ospite_nome}</p>
                <p className="truncate text-xs text-slate-500">{prossima.indirizzo}</p>
              </div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
